import React, { Ref, useEffect, useRef } from 'react';
import Button from '@alfa-retail-react/components/button';
import {
    MainScreen,
    OverContentLoadableBlock,
    PageFrame,
    ShowComponent,
    Step,
    useForm,
    useNavigate,
} from '@alfa-retail-react/components';
import { YesNoState } from '@alfa-retail-react/components/yes-no-question';
import Big from 'big.js';
import { FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';

import { ProductParametersSkeleton } from 'app/component/page/skeletons';
import { OrderService } from 'app/service/OrderService';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import { StepNames } from 'app/StepNames';
import {
    networkExceptionHandlerOnLoadData,
    networkExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/networkExceptionHandler';
import { StepOrder, TOTAL_STEP_COUNT } from 'app/constants/step';
import {
    unauthorizedExceptionHandlerOnLoadData,
    unauthorizedExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/unauthorizedExceptionHandler';
import { validationExceptionHandler } from 'app/logic/pages/error/validationExceptionHandler';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { useAnalytics } from 'app/analytics/useAnalytics';
import { LimitForm } from 'app/logic/pages/productParametersConfigure/LimitForm';
import { InsuranceForm } from 'app/logic/pages/productParametersConfigure/InsuranceForm';
import { InsuranceFormV2 } from 'app/logic/pages/productParametersConfigure/InsuranceFormV2';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/FeatureFlags';
import { ProductParametersConfigurePageStore } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigurePageStore';
import { orderRejectedExceptionHandler } from 'app/logic/pages/error/orderRejectedExceptionHandler';
import { CardOrder } from 'app/model/CardOrder';
import { PageErrors } from 'app/logic/pages/error/PageErrors';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { useI18nContext } from 'app/store/сontext/I18nContext';

import { ProductParametersConfigureFormStore } from './ProductParametersConfigureFormStore';

import 'app/logic/pages/productParametersConfigure/ProductParametersConfigurePage.less';

export type ProductParametersConfigureFormValues = {
    amount: number;
    insuranceProductCode: string;
    limit: number;
    insuranceToggle: YesNoState;
    insurance: string;
};

const CURRENT_STEP_ORDER: number = StepOrder.STEP_CHOOSE_PRODUCT_DATA;
const PAGE_NAME = 'ProductParametersConfigurePage';

export const ProductParametersConfigurePage = observer(() => {
    const applicationContext = useApplicationContext();
    const navigate = useNavigate();
    const pageStore = useRef(new ProductParametersConfigurePageStore()).current;
    const analytics = useAnalytics(
        applicationContext.productCode,
        PAGE_NAME,
        false,
    );
    const notification = useNotificationContext();
    const i18nContext = useI18nContext();

    const { cardProduct, loadStateStore, formStore, order } = pageStore;
    const { phoneNumber } = applicationContext;

    const maxAmount = ProductParametersConfigureFormStore.getMaxAmount({
        cardProduct,
        activityKindCode: order?.client?.employment?.activityKindCode,
    });

    const InsuranceFormComponent = isFeatureEnabled(
        FeatureFlag.INSURANCE_NEW_VIEW,
    )
        ? InsuranceFormV2
        : InsuranceForm;

    useEffect(() => {
        analytics.pageDisplayed();

        const orderId = sessionStorage.getItem('orderId');
        pageStore
            .initialize(
                orderId,
                applicationContext.isInitialStepVisit(CURRENT_STEP_ORDER),
            )
            .then(() => {
                if (
                    applicationContext.isFormRestoreAvailable(
                        CURRENT_STEP_ORDER,
                    )
                ) {
                    pageStore.restoreFormData(
                        applicationContext.latestFilledFormData,
                    );
                }
            })
            .catch(exceptionLogger(PAGE_NAME, 'pageStore.initialize'))
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber,
                }),
            )
            .catch(networkExceptionHandlerOnLoadData({ navigate }))
            .catch(defaultExceptionHandler(notification, analytics));
    }, []);

    const form = useForm<ProductParametersConfigureFormValues>({
        errorContextConverter: pageStore.contextConverter,
    });
    const {
        handleSubmitWithValidationError,
        pageErrors,
        setPageErrors,
        pageErrorsComponentRef,
    } = form;

    const backAction = () => {
        analytics.back();

        applicationContext.setLatestFilledFormData(
            pageStore.restorationFormData,
            CURRENT_STEP_ORDER,
        );

        navigate(-1);
    };

    const onSubmitForm = () => {
        const insuranceProductCode =
            formStore.hasAppliedInsurance && formStore.hasAvailableInsurance
                ? formStore.insuranceProduct?.code
                : null;

        const orderCopy = new CardOrder(
            order.id,
            order.saleChannel,
            order.client,
            order.cardProductCode,
            order.cardProductId,
            insuranceProductCode,
            new Big(formStore.creditLimit),
            order.currency,
            order.deliveryAddress,
            order.authenticationTimestamp,
            StepNames.STEP_CHOOSE_PRODUCT_DATA,
            order.cardholderName,
            order.state,
            order.number,
            order.source,
        );

        return pageStore.actionStateStore.loadData(() => {
            analytics.confirm();
            return new OrderService()
                .updateOrder(orderCopy)
                .then(() => {
                    applicationContext.latestSubmittedStep = CURRENT_STEP_ORDER;
                    navigate(`/${applicationContext.productCode}/confirmation`);
                })
                .catch(exceptionLogger(PAGE_NAME, 'orderService.updateOrder'))
                .catch(
                    unauthorizedExceptionHandlerOnSubmitData({
                        navigate,
                        phoneNumber: applicationContext.phoneNumber,
                    }),
                )
                .catch(
                    orderRejectedExceptionHandler(
                        navigate,
                        analytics,
                        i18nContext.t,
                    ),
                )
                .catch(
                    validationExceptionHandler(
                        form,
                        analytics,
                        pageStore.contextConverter,
                    ),
                )
                .catch(networkExceptionHandlerOnSubmitData({ setPageErrors }))
                .catch(defaultExceptionHandler(notification, analytics));
        });
    };

    return (
        <OverContentLoadableBlock show={pageStore.actionStateStore.isLoading}>
            <PageFrame
                rightAddonLogo={applicationContext.additionalLogo}
                displayFooter
                canGoPreviousPage
                backAction={backAction}
            >
                <MainScreen>
                    <MainScreen.Header
                        title={applicationContext.getCardProductTitle()}
                    />
                    <MainScreen.ContentBar>
                        <ShowComponent
                            condition={loadStateStore.isLoaded}
                            alternative={<ProductParametersSkeleton />}
                        >
                            <div className="productParametersConfigurePage">
                                <Step
                                    current={CURRENT_STEP_ORDER}
                                    total={TOTAL_STEP_COUNT}
                                />

                                <FormProvider<ProductParametersConfigureFormValues>
                                    {...form}
                                >
                                    <form
                                        onSubmit={handleSubmitWithValidationError(
                                            {
                                                onValid: onSubmitForm,
                                            },
                                        )}
                                    >
                                        <LimitForm
                                            cardProduct={cardProduct}
                                            formStore={formStore}
                                            maxAmount={maxAmount}
                                        />

                                        {pageStore.hasInsurance && (
                                            <InsuranceFormComponent
                                                pageStore={pageStore}
                                            />
                                        )}

                                        {pageErrors.length > 0 && (
                                            <PageErrors
                                                ref={
                                                    pageErrorsComponentRef as Ref<HTMLDivElement>
                                                }
                                                errors={pageErrors}
                                            />
                                        )}
                                        <Button
                                            className="submitAction"
                                            type="submit"
                                            view="accent"
                                            disabled={
                                                pageStore.actionStateStore
                                                    .isLoading
                                            }
                                        >
                                            Подтвердить
                                        </Button>
                                    </form>
                                </FormProvider>
                            </div>
                        </ShowComponent>
                    </MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
